import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled, { keyframes } from 'styled-components';
import { vocabularyActions } from '../../redux/actions';
import { vocabularyService } from '../../services/vocabulary.service';
import CropEditorModal from '../Common/CropEditorModal/CropEditorModal';

interface VocabularyItem {
  id: number;
  word: string;
  phonetic_uk?: string;
  phonetic_us?: string;
  audio_uk?: string;
  audio_us?: string;
  level?: string;
  grade_level?: number;
  is_ielts?: boolean;
  is_toeic?: boolean;
  frequency_rank?: number;
  teacher_notes?: string;
  created_at: string;
  custom_image_url?: string | null;
  image_url?: string | null;
  definitions?: Array<{
    id: number;
    part_of_speech: string;
    definition_en: string;
    definition_vi?: string;
    example_en?: string;
    example_vi?: string;
    is_primary: boolean;
  }>;
}

const VocabularyManage: React.FC = () => {
  const dispatch = useDispatch();
  const vocabularyState = useSelector((state: any) => state.vocabulary);
  const { vocabularyList, stats, pagination, loading } = vocabularyState;

  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterLevel, setFilterLevel] = useState('');
  const [filterGrade, setFilterGrade] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingWord, setEditingWord] = useState<VocabularyItem | null>(null);
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const [isAddingNewWord, setIsAddingNewWord] = useState(false);
  const [notification, setNotification] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Custom image state (for edit modal)
  const [customImgUrl, setCustomImgUrl] = useState<string | null>(null);
  const [uploadingImg, setUploadingImg] = useState(false);
  const vocabImgInputRef = useRef<HTMLInputElement>(null);
  // Remove-BG state for vocab modal
  const [vocabRembgPending, setVocabRembgPending] = useState<File | null>(null);
  const [vocabRemovingBg, setVocabRemovingBg] = useState(false);
  const [vocabCropUrl, setVocabCropUrl] = useState<string | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    word: '',
    phonetic_uk: '',
    phonetic_us: '',
    audio_uk: '',
    audio_us: '',
    level: '',
    grade_level: '',
    is_ielts: false,
    is_toeic: false,
    teacher_notes: '',
    definitions: [
      { part_of_speech: 'noun', definition_en: '', definition_vi: '', example_en: '', example_vi: '' }
    ] as Array<{
      part_of_speech: string;
      definition_en: string;
      definition_vi: string;
      example_en: string;
      example_vi: string;
    }>
  });

  // Fetch vocabularies - chỉ gọi khi page, searchTerm, level, hoặc grade thay đổi
  const fetchVocabularies = useCallback(() => {
    const params: any = { page, limit: 20 };
    if (searchTerm) params.search = searchTerm;
    if (filterLevel) params.level = filterLevel;
    if (filterGrade) params.grade = filterGrade;

    dispatch(vocabularyActions.getAll(params) as any);
  }, [page, searchTerm, filterLevel, filterGrade, dispatch]);

  // Initial load
  useEffect(() => {
    fetchVocabularies();
  }, []);

  // Handle search dengan debounce
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      setPage(1);
      // Fetch sẽ được trigger bởi useEffect bên dưới khi page/searchTerm thay đổi
    }, 500);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchTerm]);

  // Fetch khi page, searchTerm, hoặc filter thay đổi
  useEffect(() => {
    fetchVocabularies();
  }, [page, searchTerm, filterLevel, filterGrade, fetchVocabularies]);

  // Load stats
  useEffect(() => {
    dispatch(vocabularyActions.getStats() as any);
  }, [dispatch]);

  const handleCreateOrUpdate = async (e: React.FormEvent, showNotification: boolean = true) => {
    e.preventDefault();

    try {
      const payload = {
        ...formData,
        grade_level: formData.grade_level ? parseInt(formData.grade_level) : null,
      };

      if (editingWord) {
        await dispatch(vocabularyActions.updateVocabulary(editingWord.id, payload) as any);
      } else {
        await dispatch(vocabularyActions.createVocabulary(payload) as any);
      }

      setShowModal(false);
      setEditingWord(null);
      resetForm();
      fetchVocabularies();
      dispatch(vocabularyActions.getStats() as any);
    } catch (error: any) {
      // Error already handled in actions
    }
  };

  const handleDelete = async (id: number, word: string) => {
    if (!window.confirm(`Bạn có chắc muốn xóa từ "${word}"?`)) return;

    try {
      await dispatch(vocabularyActions.deleteVocabulary(id) as any);
      fetchVocabularies();
      dispatch(vocabularyActions.getStats() as any);
    } catch (error: any) {
      // Error already handled in actions
    }
  };

  const handleAddNewWordFromAPI = async () => {
    const trimmedWord = searchTerm.trim();
    if (!trimmedWord) return;

    setIsAddingNewWord(true);
    try {
      // Fetch from Free Dictionary API
      const response = await fetch(`https://api.dictionaryapi.dev/api/v2/entries/en/${trimmedWord.toLowerCase()}`);
      
      if (!response.ok) {
        setNotification({
          type: 'error',
          message: `Free Dictionary API không có từ "${trimmedWord}" 😅`
        });
        setIsAddingNewWord(false);
        setTimeout(() => setNotification(null), 3000);
        return;
      }

      const data = await response.json();
      const entry = data[0];

      // Extract data từ API
      const phonetic_uk = entry.phonetics?.find((p: any) => p.text && p.audio?.includes('uk'))?.text || '';
      const audio_uk = entry.phonetics?.find((p: any) => p.audio?.includes('uk'))?.audio || '';
      const phonetic_us = entry.phonetics?.find((p: any) => p.text && p.audio?.includes('us'))?.text || '';
      const audio_us = entry.phonetics?.find((p: any) => p.audio?.includes('us'))?.audio || '';

      // Extract definitions
      let definitions = entry.meanings?.[0]?.definitions?.slice(0, 3).map((def: any) => ({
        part_of_speech: entry.meanings?.[0]?.partOfSpeech || 'noun',
        definition_en: def.definition || '',
        definition_vi: '',
        example_en: def.example || '',
        example_vi: ''
      })) || [{ part_of_speech: 'noun', definition_en: '', definition_vi: '', example_en: '', example_vi: '' }];

      // Skip translation - definition_vi will be left empty
      console.log('⏭️ Skipping Vietnamese translation');

      const vocabularyData = {
        word: trimmedWord,
        phonetic_uk: phonetic_uk,
        audio_uk: audio_uk,
        phonetic_us: phonetic_us,
        audio_us: audio_us,
        level: 'A1',
        grade_level: 10,
        is_ielts: false,
        is_toeic: false,
        definitions: definitions,
        created_at: new Date().toISOString(),
      };

      // Call API to create
      await dispatch(vocabularyActions.createVocabulary(vocabularyData) as any);

      setNotification({
        type: 'success',
        message: `Thêm từ "${trimmedWord}" thành công! ✨`
      });

      // Reset search và reload
      setSearchTerm('');
      setPage(1);
      fetchVocabularies();
      dispatch(vocabularyActions.getStats() as any);

      setTimeout(() => setNotification(null), 3000);
    } catch (error) {
      setNotification({
        type: 'error',
        message: `Lỗi: ${error instanceof Error ? error.message : 'Không thể tìm từ'}`
      });
      setTimeout(() => setNotification(null), 3000);
    } finally {
      setIsAddingNewWord(false);
    }
  };

  const handleEdit = (word: VocabularyItem) => {
    setEditingWord(word);
    setCustomImgUrl(word.custom_image_url || null);
    setFormData({
      word: word.word,
      phonetic_uk: word.phonetic_uk || '',
      phonetic_us: word.phonetic_us || '',
      audio_uk: word.audio_uk || '',
      audio_us: word.audio_us || '',
      level: word.level || 'A1',
      grade_level: word.grade_level ? word.grade_level.toString() : '',
      is_ielts: word.is_ielts || false,
      is_toeic: word.is_toeic || false,
      teacher_notes: word.teacher_notes || '',
      definitions: word.definitions ? word.definitions.map(def => ({
        part_of_speech: def.part_of_speech,
        definition_en: def.definition_en,
        definition_vi: def.definition_vi || '',
        example_en: def.example_en || '',
        example_vi: def.example_vi || ''
      })) : [
        { part_of_speech: 'noun', definition_en: '', definition_vi: '', example_en: '', example_vi: '' }
      ]
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      word: '',
      phonetic_uk: '',
      phonetic_us: '',
      audio_uk: '',
      audio_us: '',
      level: '',
      grade_level: '',
      is_ielts: false,
      is_toeic: false,
      teacher_notes: '',
      definitions: [
        { part_of_speech: 'noun', definition_en: '', definition_vi: '', example_en: '', example_vi: '' }
      ]
    });
    setCustomImgUrl(null);
  };

  const handleVocabImgUpload = async (file: File, vocabId: number) => {
    if (!file.type.startsWith('image/')) { alert('Chỉ chấp nhận file ảnh!'); return; }
    setUploadingImg(true);
    try {
      const uploadData = new FormData();
      uploadData.append('image', file);
      const apiClient = (await import('../../helpers/apiClient')).default;
      const res = await apiClient.post(
        `/api/vocabulary/${vocabId}/custom-image`,
        uploadData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      if (res.data?.success) {
        setCustomImgUrl(res.data.data.custom_image_url);
      }
    } catch (err: any) {
      alert('Upload ảnh thất bại: ' + (err?.response?.data?.message || err.message));
    } finally {
      setUploadingImg(false);
    }
  };

  const handleVocabImgDelete = async (vocabId: number) => {
    if (!window.confirm('Xóa ảnh tùy chỉnh này?')) return;
    setUploadingImg(true);
    try {
      await (await import('../../helpers/apiClient')).default.delete(`/api/vocabulary/${vocabId}/custom-image`);
      setCustomImgUrl(null);
    } catch (err: any) {
      alert('Xóa ảnh thất bại: ' + (err?.response?.data?.message || err.message));
    } finally {
      setUploadingImg(false);
    }
  };

  // Remove background then composite on white, then upload
  // Open crop editor with any File (local object URL)
  const openVocabCropEditor = (file: File) => {
    if (vocabCropUrl) URL.revokeObjectURL(vocabCropUrl);
    setVocabCropUrl(URL.createObjectURL(file));
  };

  // Remove background then open crop editor
  const handleVocabRemoveBgAndCrop = async () => {
    if (!vocabRembgPending) return;
    const file = vocabRembgPending;
    setVocabRembgPending(null);
    setVocabRemovingBg(true);
    try {
      const { removeBackground } = await import('@imgly/background-removal');
      const resultBlob = await removeBackground(file);
      const processedFile = new File([resultBlob], 'rembg-result.png', { type: 'image/png' });
      openVocabCropEditor(processedFile);
    } catch (err: any) {
      alert('Xóa nền thất bại: ' + (err?.message || err));
    } finally {
      setVocabRemovingBg(false);
    }
  };

  // Called when CropEditorModal confirms — upload cropped file
  const handleVocabCropConfirm = async (croppedFile: File) => {
    if (!editingWord) return;
    if (vocabCropUrl) { URL.revokeObjectURL(vocabCropUrl); setVocabCropUrl(null); }
    await handleVocabImgUpload(croppedFile, editingWord.id);
  };

  const handleVocabCropCancel = () => {
    if (vocabCropUrl) URL.revokeObjectURL(vocabCropUrl);
    setVocabCropUrl(null);
  };

  const addDefinition = () => {
    setFormData({
      ...formData,
      definitions: [
        ...formData.definitions,
        { part_of_speech: 'noun', definition_en: '', definition_vi: '', example_en: '', example_vi: '' }
      ]
    });
  };

  const removeDefinition = (index: number) => {
    setFormData({
      ...formData,
      definitions: formData.definitions.filter((_, i) => i !== index)
    });
  };

  const updateDefinition = (index: number, field: string, value: string) => {
    const newDefinitions = [...formData.definitions];
    newDefinitions[index] = { ...newDefinitions[index], [field]: value };
    setFormData({ ...formData, definitions: newDefinitions });
  };

  const toggleRowExpand = (id: number) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedRows(newExpanded);
  };

  const playAudio = (audioUrl: string) => {
    if (audioUrl) {
      const audio = new Audio(audioUrl);
      audio.play().catch(err => console.error('Error playing audio:', err));
    }
  };

  return (
    <Container>
      {notification && (
        <NotificationContainer type={notification.type}>
          {notification.message}
        </NotificationContainer>
      )}
      <Header>
        <Title>Quản lý Từ vựng</Title>
        <AddButton onClick={() => { resetForm(); setEditingWord(null); setShowModal(true); }}>
          + Thêm từ mới
        </AddButton>
      </Header>
      <FilterBar>
        <TotalWordsInfo>Tổng: {stats.total} từ</TotalWordsInfo>
        <SearchInput
          type="text"
          placeholder="Tìm kiếm từ..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <FilterSelect value={filterLevel} onChange={(e) => setFilterLevel(e.target.value)}>
          <option value="">Tất cả Level</option>
          <option value="A1">A1</option>
          <option value="A2">A2</option>
          <option value="B1">B1</option>
          <option value="B2">B2</option>
          <option value="C1">C1</option>
          <option value="C2">C2</option>
        </FilterSelect>
        <FilterSelect value={filterGrade} onChange={(e) => setFilterGrade(e.target.value)}>
          <option value="">Tất cả Khối</option>
          {[6, 7, 8, 9, 10, 11, 12].map(g => (
            <option key={g} value={g}>Khối {g}</option>
          ))}
        </FilterSelect>
      </FilterBar>

      {loading ? (
        <LoadingText>Đang tải...</LoadingText>
      ) : (
        <>
          <TableWrapper>
            <Table>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Từ vựng</th>
                  <th>Phiên âm</th>
                  <th>Level</th>
                  <th>Định nghĩa</th>
                  <th>Nghĩa VN</th>
                  <th>Thao tác</th>
                </tr>
              </thead>
            <tbody>
              {vocabularyList.map((vocab: VocabularyItem) => (
                <tr key={vocab.id}>
                  <td>{vocab.id}</td>
                  <td><strong>{vocab.word}</strong></td>
                  <td>
                    {vocab.phonetic_uk && (
                      <PhoneticRow>
                        <span>UK: {vocab.phonetic_uk}</span>
                        {vocab.audio_uk && (
                          <AudioButton onClick={() => playAudio(vocab.audio_uk!)}>
                            🔊
                          </AudioButton>
                        )}
                      </PhoneticRow>
                    )}
                    {vocab.phonetic_us && (
                      <PhoneticRow>
                        <span>US: {vocab.phonetic_us}</span>
                        {vocab.audio_us && (
                          <AudioButton onClick={() => playAudio(vocab.audio_us!)}>
                            🔊
                          </AudioButton>
                        )}
                      </PhoneticRow>
                    )}
                  </td>
                  <td>{vocab.level || '-'}</td>
                  <td>
                    {vocab.definitions && vocab.definitions.length > 0 ? (
                      <DefinitionCell>
                        <DefinitionPreview expanded={expandedRows.has(vocab.id)}>
                          <strong>{vocab.definitions[0].part_of_speech}</strong>: {vocab.definitions[0].definition_en}
                        </DefinitionPreview>
                        <ExpandButton onClick={() => toggleRowExpand(vocab.id)}>
                          {expandedRows.has(vocab.id) ? '▲' : '▼'}
                        </ExpandButton>
                      </DefinitionCell>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td>
                    {vocab.definitions && vocab.definitions.length > 0 && vocab.definitions[0].definition_vi
                      ? vocab.definitions[0].definition_vi
                      : '-'
                    }
                  </td>
                  <td>
                    <ActionButton onClick={() => handleEdit(vocab)} color="#2ecc71">Sửa</ActionButton>
                    <ActionButton onClick={() => handleDelete(vocab.id, vocab.word)} color="#e74c3c">Xóa</ActionButton>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          {vocabularyList.length === 0 && searchTerm && (
            <NoResultsMessage>
              <div>
                <p>😔 Không tìm thấy từ "{searchTerm}" trong thư viện</p>
                <button 
                  onClick={handleAddNewWordFromAPI}
                  disabled={isAddingNewWord}
                  style={{
                    padding: '10px 20px',
                    marginTop: '12px',
                    backgroundColor: '#3498db',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: isAddingNewWord ? 'not-allowed' : 'pointer',
                    opacity: isAddingNewWord ? 0.7 : 1
                  }}
                >
                  {isAddingNewWord ? '⏳ Đang tìm...' : '🔍 Tìm và thêm từ này'}
                </button>
              </div>
            </NoResultsMessage>
          )}
          </TableWrapper>

          <PaginationBottom>
            <PageButton onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>
              ← Trước
            </PageButton>
            <PageInfo>
              Hiển thị {vocabularyList.length}/{pagination.total || 0} từ · Trang {page} / {pagination.totalPages || 0}
            </PageInfo>
            <PageButton onClick={() => setPage(p => Math.min(pagination.totalPages || 1, p + 1))} disabled={page === pagination.totalPages}>
              Sau →
            </PageButton>
          </PaginationBottom>
        </>
      )}

      {showModal && (
        <Modal>
          <ModalContent>
            <ModalHeader>
              <ModalTitle>{editingWord ? 'Chỉnh sửa từ vựng' : 'Thêm từ vựng mới'}</ModalTitle>
              <CloseButton onClick={() => { setShowModal(false); setEditingWord(null); }}>×</CloseButton>
            </ModalHeader>

            <Form onSubmit={handleCreateOrUpdate}>
              <FormSection>
                <SectionTitle>Thông tin cơ bản</SectionTitle>
                <FormGroup>
                  <Label>Từ vựng *</Label>
                  <Input
                    type="text"
                    value={formData.word}
                    onChange={(e) => setFormData({ ...formData, word: e.target.value })}
                    required
                    disabled={!!editingWord}
                  />
                </FormGroup>

                <FormRow>
                  <FormGroup>
                    <Label>Phiên âm UK</Label>
                    <Input
                      type="text"
                      value={formData.phonetic_uk}
                      onChange={(e) => setFormData({ ...formData, phonetic_uk: e.target.value })}
                      placeholder="/həˈləʊ/"
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>Phiên âm US</Label>
                    <Input
                      type="text"
                      value={formData.phonetic_us}
                      onChange={(e) => setFormData({ ...formData, phonetic_us: e.target.value })}
                      placeholder="/həˈloʊ/"
                    />
                  </FormGroup>
                </FormRow>

                <FormRow>
                  <FormGroup>
                    <Label>Audio URL UK</Label>
                    <Input
                      type="text"
                      value={formData.audio_uk}
                      onChange={(e) => setFormData({ ...formData, audio_uk: e.target.value })}
                      placeholder="https://..."
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>Audio URL US</Label>
                    <Input
                      type="text"
                      value={formData.audio_us}
                      onChange={(e) => setFormData({ ...formData, audio_us: e.target.value })}
                      placeholder="https://..."
                    />
                  </FormGroup>
                </FormRow>

                <FormRow>
                  <FormGroup>
                    <Label>CEFR Level *</Label>
                    <Select value={formData.level} onChange={(e) => setFormData({ ...formData, level: e.target.value })} required>
                      <option value="">-- Chọn Level --</option>
                      <option value="A1">A1</option>
                      <option value="A2">A2</option>
                      <option value="B1">B1</option>
                      <option value="B2">B2</option>
                      <option value="C1">C1</option>
                      <option value="C2">C2</option>
                    </Select>
                  </FormGroup>
                  <FormGroup>
                    <Label>Khối lớp</Label>
                    <Input
                      type="number"
                      value={formData.grade_level}
                      onChange={(e) => setFormData({ ...formData, grade_level: e.target.value })}
                      placeholder="6-12"
                      min="6"
                      max="12"
                    />
                  </FormGroup>
                </FormRow>

                <FormRow>
                  <CheckboxGroup>
                    <Checkbox
                      type="checkbox"
                      checked={formData.is_ielts}
                      onChange={(e) => setFormData({ ...formData, is_ielts: e.target.checked })}
                    />
                    <Label>Từ IELTS</Label>
                  </CheckboxGroup>
                  <CheckboxGroup>
                    <Checkbox
                      type="checkbox"
                      checked={formData.is_toeic}
                      onChange={(e) => setFormData({ ...formData, is_toeic: e.target.checked })}
                    />
                    <Label>Từ TOEIC</Label>
                  </CheckboxGroup>
                </FormRow>

                <FormGroup>
                  <Label>Ghi chú giáo viên</Label>
                  <TextArea
                    value={formData.teacher_notes}
                    onChange={(e) => setFormData({ ...formData, teacher_notes: e.target.value })}
                    placeholder="Ghi chú về cách dạy, điểm lưu ý..."
                    rows={3}
                  />
                </FormGroup>
              </FormSection>

              <FormSection>
                <SectionTitle>
                  Định nghĩa
                  <AddDefButton type="button" onClick={addDefinition}>+ Thêm nghĩa</AddDefButton>
                </SectionTitle>

                {formData.definitions.map((def, index) => (
                  <DefinitionCard key={index}>
                    <DefHeader>
                      <DefNumber>Nghĩa {index + 1}</DefNumber>
                      {formData.definitions.length > 1 && (
                        <RemoveDefButton type="button" onClick={() => removeDefinition(index)}>Xóa</RemoveDefButton>
                      )}
                    </DefHeader>

                    <FormGroup>
                      <Label>Từ loại</Label>
                      <Select
                        value={def.part_of_speech}
                        onChange={(e) => updateDefinition(index, 'part_of_speech', e.target.value)}
                      >
                        <option value="noun">Noun (Danh từ)</option>
                        <option value="verb">Verb (Động từ)</option>
                        <option value="adjective">Adjective (Tính từ)</option>
                        <option value="adverb">Adverb (Trạng từ)</option>
                        <option value="pronoun">Pronoun (Đại từ)</option>
                        <option value="preposition">Preposition (Giới từ)</option>
                        <option value="conjunction">Conjunction (Liên từ)</option>
                        <option value="interjection">Interjection (Thán từ)</option>
                      </Select>
                    </FormGroup>

                    <FormGroup>
                      <Label>Định nghĩa tiếng Anh *</Label>
                      <TextArea
                        value={def.definition_en}
                        onChange={(e) => updateDefinition(index, 'definition_en', e.target.value)}
                        placeholder="A word or phrase used when meeting someone..."
                        rows={2}
                        required
                      />
                    </FormGroup>

                    <FormGroup>
                      <Label>Định nghĩa tiếng Việt</Label>
                      <TextArea
                        value={def.definition_vi}
                        onChange={(e) => updateDefinition(index, 'definition_vi', e.target.value)}
                        placeholder="Từ hoặc cụm từ dùng khi gặp ai đó..."
                        rows={2}
                      />
                    </FormGroup>

                    <FormGroup>
                      <Label>Ví dụ tiếng Anh</Label>
                      <Input
                        type="text"
                        value={def.example_en}
                        onChange={(e) => updateDefinition(index, 'example_en', e.target.value)}
                        placeholder="Hello, nice to meet you!"
                      />
                    </FormGroup>

                    <FormGroup>
                      <Label>Ví dụ tiếng Việt</Label>
                      <Input
                        type="text"
                        value={def.example_vi}
                        onChange={(e) => updateDefinition(index, 'example_vi', e.target.value)}
                        placeholder="Xin chào, rất vui được gặp bạn!"
                      />
                    </FormGroup>
                  </DefinitionCard>
                ))}
              </FormSection>

              {editingWord && (
                <FormSection>
                  <SectionTitle>🖼️ Ảnh tùy chỉnh</SectionTitle>

                  {/* Preview */}
                  {customImgUrl && (
                    <div style={{ marginBottom: 10, position: 'relative', display: 'inline-block' }}>
                      <img
                        src={customImgUrl.startsWith('http') ? customImgUrl : `${(process.env.REACT_APP_API_URL || '').replace(/\/$/, '')}${customImgUrl}`}
                        alt="custom"
                        style={{ width: '100%', maxHeight: 180, objectFit: 'cover', borderRadius: 10, border: '2px solid #c7d2fe' }}
                      />
                      <span style={{
                        position: 'absolute', top: 6, left: 6,
                        background: 'rgba(99,102,241,0.85)', color: '#fff',
                        fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 6,
                      }}>📸 Ảnh tùy chỉnh</span>
                    </div>
                  )}

                  {/* Drop zone */}
                  <div
                    style={{ position: 'relative' }}
                  >
                    <div
                      style={{
                        border: '2px dashed #a5b4fc', borderRadius: 10, padding: '18px',
                        textAlign: 'center', background: '#f5f3ff', cursor: 'pointer',
                        color: '#7c3aed', fontSize: 13, fontWeight: 600,
                      }}
                      onClick={() => !uploadingImg && !vocabRemovingBg && vocabImgInputRef.current?.click()}
                      onDragOver={e => { e.preventDefault(); }}
                      onDrop={e => {
                        e.preventDefault();
                        const file = e.dataTransfer.files?.[0];
                        if (file && editingWord) setVocabRembgPending(file);
                      }}
                    >
                      {vocabRemovingBg ? '⏳ Đang xóa nền ảnh...' :
                        uploadingImg ? '⏳ Đang upload...' : (
                        customImgUrl ? '🔄 Kéo thả / click để đổi ảnh' : '➕ Kéo thả / click để thêm ảnh'
                      )}
                    </div>

                    {/* Remove-BG choice popup */}
                    {vocabRembgPending && editingWord && (
                      <div style={{
                        position: 'absolute', inset: 0, zIndex: 20, borderRadius: 10,
                        background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        <div style={{ background: '#fff', borderRadius: 14, padding: '20px 22px', maxWidth: 270, width: '95%', textAlign: 'center', boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}>
                          <div style={{ fontSize: '1.8rem', marginBottom: 6 }}>🖼️</div>
                          <div style={{ fontWeight: 700, fontSize: '0.95rem', marginBottom: 4, color: '#1e293b' }}>Xử lý ảnh</div>
                          <div style={{ fontSize: '0.8rem', color: '#64748b', marginBottom: 14 }}>
                            Chọn cách xử lý ảnh bạn vừa chọn:
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                            <button
                              type="button"
                              onClick={handleVocabRemoveBgAndCrop}
                              style={{ padding: '9px 0', borderRadius: 9, border: 'none', background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', color: '#fff', fontWeight: 700, fontSize: '0.88rem', cursor: 'pointer' }}
                            >
                              ✨ Xóa nền + Crop
                            </button>
                            <button
                              type="button"
                              onClick={() => { const f = vocabRembgPending!; setVocabRembgPending(null); openVocabCropEditor(f); }}
                              style={{ padding: '8px 0', borderRadius: 9, border: '1.5px solid #c7d2fe', background: '#f0f4ff', color: '#4f46e5', fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer' }}
                            >
                              ✂️ Crop thôi
                            </button>
                            <button
                              type="button"
                              onClick={async () => { const f = vocabRembgPending!; setVocabRembgPending(null); await handleVocabImgUpload(f, editingWord.id); }}
                              style={{ padding: '8px 0', borderRadius: 9, border: '1.5px solid #d1fae5', background: '#f0fdf4', color: '#059669', fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer' }}
                            >
                              ⬆️ Upload thẳng
                            </button>
                            <button
                              type="button"
                              onClick={() => setVocabRembgPending(null)}
                              style={{ padding: '7px 0', borderRadius: 9, border: '1px solid #e2e8f0', background: '#fff', color: '#94a3b8', fontWeight: 500, fontSize: '0.82rem', cursor: 'pointer' }}
                            >
                              Hủy
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  <input
                    ref={vocabImgInputRef}
                    type="file" accept="image/*"
                    style={{ display: 'none' }}
                    onChange={e => {
                      const file = e.target.files?.[0];
                      if (file && editingWord) setVocabRembgPending(file);
                      e.target.value = '';
                    }}
                  />

                  {customImgUrl && (
                    <button
                      type="button"
                      onClick={() => editingWord && handleVocabImgDelete(editingWord.id)}
                      disabled={uploadingImg || vocabRemovingBg}
                      style={{
                        marginTop: 8, padding: '6px 16px', borderRadius: 8, border: 'none',
                        background: '#fef2f2', color: '#ef4444', fontWeight: 600,
                        fontSize: 12, cursor: 'pointer',
                      }}
                    >
                      🗑️ Xóa ảnh tùy chỉnh
                    </button>
                  )}
                </FormSection>
              )}

              <FormActions>
                <CancelButton type="button" onClick={() => { setShowModal(false); setEditingWord(null); }}>
                  Hủy
                </CancelButton>
                <SubmitButton type="submit" disabled={loading}>
                  {loading ? 'Đang xử lý...' : (editingWord ? 'Cập nhật' : 'Thêm mới')}
                </SubmitButton>
              </FormActions>
            </Form>
          </ModalContent>
        </Modal>
      )}
      {/* ─── Rembg loading overlay (full-screen, outside Modal) ─── */}
      {vocabRemovingBg && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 9998,
          background: 'rgba(0,0,0,0.65)',
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16,
        }}>
          <div style={{ fontSize: '3rem' }}>⚙️</div>
          <div style={{ color: '#fff', fontWeight: 700, fontSize: '1.1rem' }}>Đang xóa nền ảnh...</div>
          <div style={{ color: '#a5b4fc', fontSize: '0.85rem' }}>Vui lòng đợi, lần đầu tải model AI có thể mất 10–20 giây</div>
        </div>
      )}

      {/* ─── Crop editor modal ─── */}
      {vocabCropUrl && (
        <CropEditorModal
          imageUrl={vocabCropUrl}
          onConfirm={handleVocabCropConfirm}
          onCancel={handleVocabCropCancel}
        />
      )}
    </Container>
  );
};

// Styled Components
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const Container = styled.div`
  padding: 20px;
  max-width: 1400px;
  margin: 0 auto;
  animation: ${fadeIn} 0.5s ease-out;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const Title = styled.h1`
  font-size: 28px;
  color: #2c3e50;
  margin: 0;
`;

const AddButton = styled.button`
  background: #27ae60;
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 6px;
  font-size: 16px;
  cursor: pointer;
  font-weight: 600;
  transition: background 0.2s;

  &:hover {
    background: #229954;
  }
`;

const StatsBar = styled.div`
  display: flex;
  gap: 20px;
  margin-bottom: 20px;
  padding: 16px;
  background: #ecf0f1;
  border-radius: 8px;
  flex-wrap: wrap;
`;

const StatItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StatLabel = styled.span`
  color: #7f8c8d;
  font-size: 14px;
`;

const StatValue = styled.span`
  color: #2c3e50;
  font-weight: 600;
  font-size: 18px;
`;

const FilterBar = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 20px;
  align-items: center;
`;

const SearchInput = styled.input`
  flex: 1;
  padding: 10px 16px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;

  &:focus {
    outline: none;
    border-color: #3498db;
  }
`;

const TotalWordsInfo = styled.div`
  white-space: nowrap;
  font-size: 14px;
  color: #34495e;
  font-weight: 500;
  padding: 8px 12px;
  background: #ecf0f1;
  border-radius: 6px;
`;

const FilterSelect = styled.select`
  padding: 10px 16px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  background: white;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: #3498db;
  }
`;

const LoadingText = styled.div`
  text-align: center;
  padding: 40px;
  color: #7f8c8d;
  font-size: 16px;
`;

const NoResultsMessage = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px 20px;
  background: white;
  color: #34495e;
  font-size: 16px;
  text-align: center;

  div {
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  p {
    margin: 0;
    font-size: 16px;
    color: #7f8c8d;
  }
`;

const NotificationContainer = styled.div<{ type: 'success' | 'error' }>`
  position: fixed;
  top: 20px;
  right: 20px;
  padding: 16px 20px;
  background: ${props => props.type === 'success' ? '#2ecc71' : '#e74c3c'};
  color: white;
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  animation: slideIn 0.3s ease-in-out;

  @keyframes slideIn {
    from {
      transform: translateX(400px);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
`;

const PaginationBottom = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  margin-top: 16px;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 6px;
`;

const TableWrapper = styled.div`
  max-height: 600px;
  overflow-y: auto;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border: 1px solid #ddd;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
  }

  &::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #555;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: white;

  th {
    background: #34495e;
    color: white;
    padding: 12px 16px;
    text-align: left;
    font-weight: 600;
    font-size: 14px;
    position: sticky;
    top: 0;
    z-index: 10;
  }

  td {
    padding: 12px 16px;
    border-bottom: 1px solid #ecf0f1;
    font-size: 14px;
  }

  tr:hover {
    background: #f8f9fa;
  }
`;

const Tag = styled.span<{ color: string }>`
  display: inline-block;
  padding: 4px 8px;
  background: ${props => props.color};
  color: white;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  margin-right: 4px;
`;

const PhoneticRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const AudioButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  font-size: 16px;
  padding: 0;
  line-height: 1;
  transition: transform 0.2s;

  &:hover {
    transform: scale(1.2);
  }

  &:active {
    transform: scale(0.9);
  }
`;

const DefinitionCell = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  max-width: 400px;
`;

const DefinitionPreview = styled.div<{ expanded?: boolean }>`
  flex: 1;
  font-size: 13px;
  color: #555;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: ${props => props.expanded ? 'unset' : '2'};
  -webkit-box-orient: vertical;
  text-overflow: ${props => props.expanded ? 'unset' : 'ellipsis'};
`;

const ExpandButton = styled.button`
  background: none;
  border: none;
  color: #3498db;
  cursor: pointer;
  padding: 0;
  font-size: 12px;
  line-height: 1;
  min-width: 20px;

  &:hover {
    color: #2980b9;
  }
`;

const ActionButton = styled.button<{ color: string }>`
  background: ${props => props.color};
  color: white;
  border: none;
  padding: 6px 12px;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  margin-right: 6px;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.8;
  }
`;

const Pagination = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  margin-top: 24px;
`;

const PageButton = styled.button`
  background: #3498db;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;

  &:disabled {
    background: #bdc3c7;
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    background: #2980b9;
  }
`;

const PageInfo = styled.span`
  color: #7f8c8d;
  font-size: 14px;
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 12px;
  width: 100%;
  max-width: 800px;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid #ecf0f1;
`;

const ModalTitle = styled.h2`
  margin: 0;
  color: #2c3e50;
  font-size: 22px;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 32px;
  color: #7f8c8d;
  cursor: pointer;
  padding: 0;
  line-height: 1;

  &:hover {
    color: #2c3e50;
  }
`;

const Form = styled.form`
  padding: 24px;
`;

const FormSection = styled.div`
  margin-bottom: 32px;
`;

const SectionTitle = styled.h3`
  color: #2c3e50;
  font-size: 18px;
  margin: 0 0 16px 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const FormGroup = styled.div`
  margin-bottom: 16px;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 6px;
  color: #34495e;
  font-size: 14px;
  font-weight: 500;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #3498db;
  }

  &:disabled {
    background: #f8f9fa;
    cursor: not-allowed;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  background: white;
  cursor: pointer;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #3498db;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  resize: vertical;
  font-family: inherit;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #3498db;
  }
`;

const CheckboxGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Checkbox = styled.input`
  width: 18px;
  height: 18px;
  cursor: pointer;
`;

const AddDefButton = styled.button`
  background: #3498db;
  color: white;
  border: none;
  padding: 6px 12px;
  border-radius: 4px;
  font-size: 13px;
  cursor: pointer;

  &:hover {
    background: #2980b9;
  }
`;

const DefinitionCard = styled.div`
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
  background: #f8f9fa;
`;

const DefHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
`;

const DefNumber = styled.div`
  font-weight: 600;
  color: #2c3e50;
  font-size: 15px;
`;

const RemoveDefButton = styled.button`
  background: #e74c3c;
  color: white;
  border: none;
  padding: 4px 12px;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;

  &:hover {
    background: #c0392b;
  }
`;

const FormActions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  padding-top: 16px;
  border-top: 1px solid #ecf0f1;
`;

const CancelButton = styled.button`
  background: #95a5a6;
  color: white;
  border: none;
  padding: 10px 24px;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
  font-weight: 500;

  &:hover {
    background: #7f8c8d;
  }
`;

const SubmitButton = styled.button`
  background: #27ae60;
  color: white;
  border: none;
  padding: 10px 24px;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
  font-weight: 500;

  &:hover {
    background: #229954;
  }

  &:disabled {
    background: #bdc3c7;
    cursor: not-allowed;
  }
`;

export default VocabularyManage;
